import type { ChartDataPoint } from 'features/systemLens'

/**
 * Values for the agent's rolling-average pens.
 *
 * These cannot be derived values: the chart's formula evaluator sees one
 * timestamp at a time, so nothing it can express looks backwards. They were
 * briefly implemented as a smoothing transform on the source tag, which put the
 * numbers on screen but made the average an attribute of that tag — it had no
 * row of its own, no visibility of its own, and it vanished when the tag it was
 * computed from was hidden.
 *
 * So it is computed here instead and merged into its own column, the same way a
 * saved model's expected and residual series are. A placeholder derived value
 * carries the pen's identity (name, unit, dependencies) and this supplies the
 * numbers.
 */

export interface AveragePenSeries {
  /** The derived-value id this fills. */
  id: string
  /** The column averaged — a tag, or another derived pen. */
  sourceId: string
  windowMs: number
}

let series: AveragePenSeries[] = []
const listeners = new Set<() => void>()

const same = (a: AveragePenSeries[], b: AveragePenSeries[]): boolean =>
  a.length === b.length &&
  a.every(
    (spec, i) =>
      spec.id === b[i].id &&
      spec.sourceId === b[i].sourceId &&
      spec.windowMs === b[i].windowMs
  )

export const setAgentPenSeries = (next: AveragePenSeries[]): void => {
  if (same(series, next)) return
  series = next
  listeners.forEach((listener) => listener())
}

export const getAgentPenSeries = (): AveragePenSeries[] => series

export const subscribeAgentPenSeries = (listener: () => void): (() => void) => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/**
 * Fill each pen's column with a trailing time-window mean of its source.
 *
 * Windows are measured over the SOURCE's own observations rather than over
 * chart rows, because a multi-cadence chart's rows hold other pens' samples and
 * counting those would make the window depend on what else is plotted.
 *
 * Pure, and takes its specs as an argument, so the caller's memo can depend on
 * them and a test does not need the store.
 */
export const applyAgentPenSeries = (
  data: ChartDataPoint[],
  specs: AveragePenSeries[]
): ChartDataPoint[] => {
  if (!specs.length || !data.length) return data
  // Copy-on-write like applyModelSeries: only sourced rows gain a column, so
  // only those rows are cloned.
  const out = data.slice()

  for (const spec of specs) {
    const rows: number[] = []
    const stamps: number[] = []
    const values: number[] = []
    out.forEach((point, index) => {
      const value = point[spec.sourceId]
      if (typeof value !== 'number' || !Number.isFinite(value)) return
      rows.push(index)
      stamps.push(point.timestamp)
      values.push(value)
    })
    if (!rows.length) continue

    // Two pointers over the source's samples: the window start only ever moves
    // forward, so this is one pass rather than a scan per point.
    //
    // The window is half-open, `(t - windowMs, t]`. Keeping the sample exactly
    // one window old would put 25 hourly points in a "24-hour average", and
    // half-open is also what pandas does — which is what the backend's own
    // rolling median uses, so the two agree about what "24h" means.
    let head = 0
    let sum = 0
    for (let tail = 0; tail < rows.length; tail++) {
      sum += values[tail]
      while (stamps[head] <= stamps[tail] - spec.windowMs) {
        sum -= values[head]
        head += 1
      }
      out[rows[tail]] = {
        ...out[rows[tail]],
        [spec.id]: sum / (tail - head + 1),
      }
    }
  }

  return out
}
